import { CreditCard } from 'lucide-react';

/**
 * Shared Stripe / HighCall provider badge.
 *
 * Extracted from the profile Transaction-history tab so the same "how was
 * this handled" visual reads identically across all the consolidated
 * financial surfaces (Event History rows on the user profile + admin user
 * details, the transaction ledger, etc). A `stripe` badge means money moved
 * through Stripe (card or ACH); a `highcall` badge means it was settled
 * off-Stripe and recorded by HighCall (cash, check, or a manual ledger
 * entry).
 */

// Stripe wordmark, inlined from their public brand assets. Uses currentColor
// so it inherits the badge's indigo text color and reads crisply on both
// light and dark themes — no PNG fetch, no DPR scaling issues.
function StripeWordmark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 60 25"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      focusable="false"
      className={className}
    >
      <path
        d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.91-1.04l-.02 4.63-4.12.87V5.57h3.63l.21 1.03c.57-.53 1.61-1.31 3.22-1.31 2.89 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.63 7.61zm-.96-11.36c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.27 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.46 1.46zm-8.55 4.78c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.4h-3.13v5.6zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66-1.4 0-2.95-.27-4.48-.93v-3.93c1.38.75 3.1 1.31 4.48 1.31.93 0 1.6-.25 1.6-1.02 0-1.98-6.26-1.23-6.26-5.79 0-2.92 2.23-4.66 5.57-4.66 1.34 0 2.69.2 4.03.74v3.88a9.18 9.18 0 0 0-4.03-1.04c-.88 0-1.43.25-1.43.9 0 1.86 6.25 1.06 6.25 5.88z"
        fill="currentColor"
      />
    </svg>
  );
}

export type Provider = 'stripe' | 'highcall';

export function ProviderBadge({ provider }: { provider: Provider }) {
  if (provider === 'stripe') {
    // CreditCard glyph + Stripe wordmark. The HighCall badge's "HIGHCALL"
    // text inherits ~3px of invisible leading from text-[10px]'s default
    // line-height; the wordmark SVG is pixel-tight. Bump py/px on the Stripe
    // badge only to equalize the perceived inner padding without making it
    // taller than the HighCall badge.
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-indigo-500/30 bg-indigo-500/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300">
        <CreditCard className="h-3.5 w-3.5" />
        <StripeWordmark className="h-3 w-auto" />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md border border-foreground/20 bg-foreground/5 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-foreground/80">
      <img
        src="/icons/HighCallRodeo-icon-red-56.png"
        alt=""
        aria-hidden
        className="h-3.5 w-3.5 object-contain"
      />
      HighCall
    </span>
  );
}

/**
 * Derive which provider settled (or will settle) a registration-style row
 * from its persisted payment method. Returns null when nothing has been
 * handled yet (pay-later / unknown) so callers can omit the badge rather
 * than imply a settlement that hasn't happened.
 *
 * Accepts the loosely-typed method strings used across the app:
 *   - user RegistrationRow.payment_method: 'stripe' | 'paypal' | 'cash' |
 *     'check' | 'pay_later'
 *   - admin AdminEntryRow.payment_method: 'stripe_card' | 'bank' | 'cash' |
 *     'check' | 'pay_later'
 */
export function deriveProvider(
  method: string | null | undefined,
  opts?: { hasStripeMethodType?: boolean },
): Provider | null {
  if (opts?.hasStripeMethodType) return 'stripe';
  switch (method) {
    case 'stripe':
    case 'stripe_card':
    case 'bank':
    case 'paypal':
      return 'stripe';
    case 'cash':
    case 'check':
      return 'highcall';
    default:
      return null;
  }
}
